import React, { useState, memo } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios"; 
import { Loader2, Mail, Lock, PenTool, XCircle, Sparkles, ArrowRight } from "lucide-react";

/**
 * Mekânname Editör/Küratör Giriş Sayfası
 */
function EditorLogin() {
  const navigate = useNavigate();
  
  // Form State'leri
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  
  // UI State'leri
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const API_URL = process.env.REACT_APP_API_URL;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/editor/login`, {
        email: email,
        password: password
      });

      const data = response.data;

      localStorage.setItem("token", data.access_token);
      localStorage.setItem("userRole", data.role);

      navigate("/editor/create-route");

    } catch (err) {
      if (err.response) {
        setError(err.response.data.detail || "Giriş yapılamadı. Bilgilerinizi kontrol edin.");
      } else if (err.request) {
        setError("Sunucuya bağlanılamadı. Lütfen internet bağlantınızı kontrol edin.");
      } else {
        setError("Beklenmedik bir hata oluştu.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-4 font-sans text-gray-50">
      <div className="w-full max-w-md bg-gray-800 p-8 rounded-3xl shadow-2xl border-t-4 border-t-indigo-500 border-l border-r border-b border-gray-700">
        
        {/* --- Header --- */}
        <header className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="p-3 bg-indigo-500/10 rounded-full">
               <PenTool className="h-8 w-8 text-indigo-400" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-50">Editör Girişi</h2>
          <p className="text-gray-400 mt-2 text-sm">
            Mekânname içerik yönetim paneli
          </p>
        </header>

        {/* --- Hata Mesajı --- */}
        {error && (
          <div className="flex items-center gap-3 p-4 mb-6 text-red-400 bg-red-900/30 rounded-xl border border-red-700 animate-pulse">
            <XCircle className="h-6 w-6 flex-shrink-0" />
            <span className="font-medium text-sm">{error}</span>
          </div>
        )}

        {/* --- Giriş Formu --- */}
        <form onSubmit={handleLogin} className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium text-gray-300">Editör E-Posta</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                id="email"
                placeholder="editor@mekanname.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full pl-10 pr-4 py-3 bg-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 border border-gray-600 transition-colors"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="password" className="text-sm font-medium text-gray-300">Şifre</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="password"
                id="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full pl-10 pr-4 py-3 bg-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 border border-gray-600 transition-colors"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full flex items-center justify-center gap-2 px-6 py-3 font-semibold rounded-lg transition-colors shadow-lg
              ${loading 
                ? 'bg-indigo-900 text-indigo-200 cursor-not-allowed' 
                : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-500/30'}`}
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" /> Kimlik Doğrulanıyor...
              </>
            ) : (
              <>
                Panele Giriş Yap
              </>
            )}
          </button>
        </form>

        {/* --- YENİ EKLENEN KISIM: Başvuru Yönlendirmesi --- */}
        <div className="mt-8 pt-6 border-t border-gray-700">
          <div className="bg-gray-700/30 p-4 rounded-xl border border-gray-700 hover:border-amber-500/50 transition-colors group">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-500/10 rounded-lg text-amber-500 mt-1">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-gray-200 group-hover:text-amber-400 transition-colors">
                  İçerik Üreticimiz Olun
                </h4>
                <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                  Mekanname ailesine katılmak ve kendi rotalarınızı paylaşmak ister misiniz?
                </p>
                <Link 
                  to="/editor-basvuru-formu" 
                  className="inline-flex items-center gap-1 text-xs font-semibold text-amber-500 mt-2 hover:text-amber-400 transition-colors"
                >
                  Başvuru Formunu Doldur <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* --- Footer Link --- */}
        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-gray-500 hover:text-indigo-400 transition-colors">
            ← Standart Kullanıcı Girişi
          </Link>
        </div>

      </div>
    </div>
  );
}

export default memo(EditorLogin);